import { useState, useCallback, useRef, useEffect } from "react";
import useAuth from "../context";

/**
 * useAuthAccessApi
 *
 * A fetch wrapper hook with authentication headers and state management.
 * Handles standardized API response format with automatic data extraction.
 *
 * Expected API Response Format:
 *   Success: { success: true, error: false, message: string, data: any, status: number }
 *   Error:   { success: false, error: true, code: string, status: number, message: string, error_trace: string, data: null }
 *
 * The hook automatically:
 * - Extracts `data` field from successful responses
 * - Throws errors for responses with error: true or success: false
 * - Passes auth headers (Bearer token, device ID)
 * - Manages loading/error states
 *
 * Features:
 * - Automatic auth headers (Bearer token, device ID)
 * - Loading, data, and error state management
 * - Support for all HTTP methods (GET, POST, PUT, PATCH, DELETE, etc.)
 * - Automatic Content-Type handling (JSON vs FormData)
 * - Query parameters support
 * - Request cancellation via AbortController
 * - Standardized error handling with code, message, and trace
 *
 * Usage:
 *   const { data, loading, error, execute, cancel } = useAuthAccessApi();
 *
 *   // Simple GET request
 *   execute({ url: '/api/users' });
 *
 *   // POST with JSON body
 *   execute({
 *     url: '/api/users',
 *     method: 'POST',
 *     body: { name: 'John', email: 'john@example.com' }
 *   });
 *
 *   // POST with FormData
 *   const formData = new FormData();
 *   formData.append('file', file);
 *   execute({
 *     url: '/api/upload',
 *     method: 'POST',
 *     body: formData
 *   });
 *
 *   // With query parameters
 *   execute({
 *     url: '/api/users',
 *     params: { page: 1, limit: 10, status: 'active' }
 *   });
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback on successful request (data, response, fullResponse)
 * @param {Function} options.onError - Callback on error (error with code, message, errorTrace)
 * @returns {Object} { data, loading, error, execute, cancel, reset }
 */
export default function useAuthAccessApi({ onSuccess, onError } = {}) {
    const auth = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            // Cancel any pending request on unmount
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    /**
     * Build URL with query parameters
     */
    const buildUrl = useCallback((url, params) => {
        if (!params || Object.keys(params).length === 0) {
            return url;
        }

        const urlObj = new URL(url, window.location.origin);
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
                urlObj.searchParams.append(key, String(value));
            }
        });

        // If the original URL was absolute, return absolute; otherwise relative
        return url.startsWith("http")
            ? urlObj.href
            : urlObj.pathname + urlObj.search;
    }, []);

    /**
     * Execute the fetch request
     */
    const execute = useCallback(
        async ({
            url,
            method = "GET",
            body = null,
            params = null,
            headers: customHeaders = {},
            ...fetchOptions
        } = {}) => {
            if (!url) {
                const err = new Error("URL is required");
                setError(err);
                if (onError) onError(err);
                return null;
            }

            // Cancel any pending request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new AbortController for this request
            abortControllerRef.current = new AbortController();

            setLoading(true);
            setError(null);

            try {
                // Build URL with query params if provided
                const finalUrl = buildUrl(url, params);

                // Prepare headers
                const headers = {
                    ...(method !== "GET" && body instanceof FormData
                        ? {}
                        : { "Content-Type": "application/json" }),
                    Authorization: `Bearer ${auth.accessToken}`,
                    "X-CC-Device-Id": auth.deviceId,
                    ...customHeaders,
                };

                // Prepare request body
                let requestBody = null;
                if (body !== null && method !== "GET") {
                    if (body instanceof FormData) {
                        requestBody = body;
                    } else if (typeof body === "object") {
                        requestBody = JSON.stringify(body);
                    } else {
                        requestBody = body;
                    }
                }

                // Execute fetch
                const response = await fetch(finalUrl, {
                    method,
                    headers,
                    body: requestBody,
                    signal: abortControllerRef.current.signal,
                    ...fetchOptions,
                });

                // Check if response is ok
                if (!response.ok) {
                    const errorText = await response.text();
                    let errorData;
                    try {
                        errorData = JSON.parse(errorText);
                    } catch {
                        errorData = {
                            message: errorText || response.statusText,
                        };
                    }

                    const err = new Error(
                        errorData.message ||
                            `HTTP error! status: ${response.status}`
                    );
                    err.response = response;
                    err.data = errorData;
                    err.status = response.status;
                    throw err;
                }

                // Parse response
                const contentType = response.headers.get("content-type");
                let responseData;

                if (contentType?.includes("application/json")) {
                    responseData = await response.json();
                } else if (contentType?.includes("text/")) {
                    responseData = await response.text();
                } else {
                    responseData = await response.blob();
                }

                // Handle standardized API response format
                // Expected format: { success, error, message, data, status }
                if (
                    responseData &&
                    typeof responseData === "object" &&
                    !Array.isArray(responseData) &&
                    !(responseData instanceof Blob)
                ) {
                    // Check for error response
                    if (
                        responseData.error === true ||
                        responseData.success === false
                    ) {
                        const err = new Error(
                            responseData.message ||
                                responseData.error_trace ||
                                "API request failed"
                        );
                        err.response = response;
                        err.data = responseData;
                        err.status = responseData.status || response.status;
                        err.code = responseData.code;
                        err.errorTrace = responseData.error_trace;
                        throw err;
                    }

                    // Extract actual data from the wrapper
                    // If the response has a 'data' field, use that as the actual data
                    if (
                        Object.prototype.hasOwnProperty.call(
                            responseData,
                            "data"
                        )
                    ) {
                        const extractedData = responseData.data;

                        // Only update state if component is still mounted
                        if (mountedRef.current) {
                            setData(extractedData);
                            setLoading(false);
                            if (onSuccess)
                                onSuccess(
                                    extractedData,
                                    response,
                                    responseData
                                );
                        }

                        return extractedData;
                    }
                }

                // Fallback for non-standard or blob responses
                if (mountedRef.current) {
                    setData(responseData);
                    setLoading(false);
                    if (onSuccess) onSuccess(responseData, response);
                }

                return responseData;
            } catch (err) {
                // Don't set error state if request was cancelled
                if (err.name === "AbortError") {
                    console.log("Request cancelled");
                    return null;
                }

                if (mountedRef.current) {
                    setError(err);
                    setLoading(false);
                    if (onError) onError(err);
                }

                throw err;
            }
        },
        [auth.accessToken, auth.deviceId, buildUrl, onSuccess, onError]
    );

    /**
     * Cancel the current request
     */
    const cancel = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setLoading(false);
        }
    }, []);

    /**
     * Reset the state
     */
    const reset = useCallback(() => {
        setData(null);
        setError(null);
        setLoading(false);
    }, []);

    return {
        data,
        loading,
        error,
        execute,
        cancel,
        reset,
    };
}

/**
 * Convenience hooks for specific HTTP methods
 */

export function useAuthGet(url, options = {}) {
    const api = useAuthAccessApi(options);
    const executeGet = useCallback(
        (params) => api.execute({ url, method: "GET", params }),
        [api, url]
    );

    return { ...api, executeGet };
}

export function useAuthPost(url, options = {}) {
    const api = useAuthAccessApi(options);
    const executePost = useCallback(
        (body, params) => api.execute({ url, method: "POST", body, params }),
        [api, url]
    );

    return { ...api, executePost };
}

export function useAuthPut(url, options = {}) {
    const api = useAuthAccessApi(options);
    const executePut = useCallback(
        (body, params) => api.execute({ url, method: "PUT", body, params }),
        [api, url]
    );

    return { ...api, executePut };
}

export function useAuthPatch(url, options = {}) {
    const api = useAuthAccessApi(options);
    const executePatch = useCallback(
        (body, params) => api.execute({ url, method: "PATCH", body, params }),
        [api, url]
    );

    return { ...api, executePatch };
}

export function useAuthDelete(url, options = {}) {
    const api = useAuthAccessApi(options);
    const executeDelete = useCallback(
        (params) => api.execute({ url, method: "DELETE", params }),
        [api, url]
    );

    return { ...api, executeDelete };
}
